import { Injectable, inject } from '@angular/core';
import { ColorsetStore } from '../../stores/colorset/colorset.state';
import { IndicatorStore } from '../../stores/indicator/indicator.state';
import { IndicatorDataStore } from '../../stores/indicatpr-data/indicator-data.state';
import { ScaleService } from '../scale.service';
import { Colorset, fallbackColors } from '../../models/colorset';
import { Indicator } from '../../models/indicator';
import { DataEntry, IndicatorData } from '../../models/indicator-data';
import { Scale } from '../../models/scale';
import { MapLegendScope } from '../../models/commune';
import { ScaleMode, PoiConf } from '../grid-widget-config.service';

@Injectable({
  providedIn: 'root',
})
export class MapDataHelper {
  private readonly colorsetStore = inject(ColorsetStore);
  private readonly indicatorStore = inject(IndicatorStore);
  private readonly scaleService = inject(ScaleService);
  private readonly dataStore = inject(IndicatorDataStore);

  /**
   * Retrieves a set of colors and color configurations based on provided input data.
   *
   * @param {IndicatorData | null} indicatorData - The data object associated with the indicator, containing color set information.
   * @param {Indicator | null} indicator - The indicator object, which may include a default color set and inversion configuration.
   * @param {string} [configColorset] - An optional configuration-provided colorset to be considered during color retrieval.
   * @param {string} [mapColorset] - An optional map-provided colorset to be considered during color retrieval.
   * @param {boolean} [invert] - An optional flag to determine if the color order should be inverted.
   * @return {{ colorset: Colorset | null, colors: string[] }} An object containing the resolved colorset and an array of colors.
   */
  getColors(
    indicatorData: IndicatorData | null,
    indicator: Indicator | null,
    configColorset?: string,
    mapColorset?: string,
    invert?: boolean
  ): { colorset: Colorset | null; colors: string[] } {
    const parent = indicator?.parent
      ? this.indicatorStore.getById(indicator.parent)
      : undefined;

    const colorset: Colorset | null = this.colorsetStore.getFirstByIds([
      indicatorData?.colorSet || '',
      indicator?.defaultColorSet || '',
      parent?.defaultColorSet || '',
      configColorset || '',
      mapColorset || '',
    ]);

    const shouldInvert = !!(
      (indicatorData && indicatorData.invert) ||
      (indicator && indicator.defaultInvert) ||
      invert
    );

    let colors: string[] = [...(colorset?.colors || fallbackColors)];
    if (shouldInvert) colors = colors.reverse();

    return { colorset, colors };
  }

  /**
   * Retrieves the colors associated with the Points of Interest (POI) configuration.
   * If a custom color set is defined in the POI configuration, it uses those colors.
   * Otherwise, it defaults to the provided default colors.
   * Optionally inverts the order of colors if specified in the configuration.
   *
   * @param {PoiConf} poiConfig - The configuration object for Points of Interest, which includes legend and color settings.
   * @param {string[]} defaultColors - The fallback array of colors to use if no custom colors are defined in the configuration.
   * @return {string[]} An array of colors configured for Points of Interest, potentially modified based on the inversion setting.
   */
  getPoiColors(poiConfig: PoiConf, defaultColors: string[]): string[] {
    const colors = poiConfig?.legend?.colorset
      ? this.colorsetStore.getById(poiConfig.legend.colorset)?.colors ||
        defaultColors
      : defaultColors;
    const copy = [...colors];
    if (poiConfig && poiConfig.legend && poiConfig.legend.invert) {
      copy.reverse();
    }
    return copy;
  }

  /**
   * Generates and retrieves scales based on the provided data, indicators, and options.
   *
   * @param {IndicatorData | null} indicatorData - The data associated with the indicators, including colors and legend information.
   * @param {Indicator | null} indicator - The indicator for which scales are to be retrieved.
   * @param {DataEntry | undefined} data - The data entry used for scale generation when other sources are unavailable.
   * @param {string[]} colors - The color set to be used for generating scales.
   * @param {Object} [legendOptions] - Optional settings for generating scales, such as scope, mode, and the number of scales.
   * @param {MapLegendScope} [legendOptions.legendScope] - Specifies the scope of the legend (e.g., all, subset-based).
   * @param {ScaleMode} [legendOptions.scaleMode] - The mode to be used for scale generation (e.g., continuous, discrete).
   * @param {number} [legendOptions.numberOfScales] - The desired number of scales to generate.
   * @param {Scale[]} [existingScales] - An array of existing scales to reference or modify.
   * @return {Scale[] | undefined} Returns an array of scales, or undefined if no scales are generated or available.
   */
  getScales(
    indicatorData: IndicatorData | null,
    indicator: Indicator | null,
    data: DataEntry | undefined,
    colors: string[],
    legendOptions?: {
      legendScope?: MapLegendScope;
      scaleMode?: ScaleMode;
      numberOfScales?: number;
    },
    existingScales?: Scale[]
  ): Scale[] | undefined {
    const indicatorScales = indicator
      ? this.indicatorStore.getScales(indicator)
      : undefined;

    return indicatorData &&
      indicatorData.colorSet &&
      indicatorData.legend &&
      indicatorData.legend.length
      ? indicatorData.legend
      : indicatorScales
        ? indicatorScales
        : data && Object.keys(data).length
          ? this.scaleService.generateScales(
              indicatorData && legendOptions?.legendScope === 'all'
                ? indicatorData.data
                : data,
              colors,
              legendOptions?.scaleMode,
              legendOptions?.numberOfScales,
              existingScales
            )
          : undefined;
  }

  /**
   * Retrieves detailed information about a specified indicator, including its metadata and associated data.
   *
   * @param {string} indicatorId - The unique identifier of the indicator.
   * @param {string} dataLayerId - The unique identifier of the data layer associated with the indicator.
   * @return {{indicator: Indicator | null, indicatorData: IndicatorData | null}} An object containing the indicator and its associated data. Returns `null` for each property if the data is not found.
   */
  getIndicatorInfo(
    indicatorId: string,
    dataLayerId: string
  ): {
    indicator: Indicator | null;
    indicatorData: IndicatorData | null;
  } {
    const indicator = this.indicatorStore.getById(indicatorId);
    const indicatorData = this.dataStore.getByDataLayerAndIndicator(
      dataLayerId,
      indicatorId
    );

    return { indicator, indicatorData };
  }
}
